import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import { connect } from 'react-redux';
import {
  Card,
  Row,
  Table,
  Col,
  Spin,
  Icon,
  Button,
  message,
} from 'antd';
import { withRouter } from 'react-router-dom';
import queryString from 'querystring';
import { CSVLink } from 'react-csv';
import {
  ROLES,
  PAGES,
  TRANSFER_APPROVAL_STATUSES,
} from 'appconstants';
import RBAC from 'components/rbac';
import { SpButton, SpText } from 'components/DesignKit';
import { transferApprovalActions, transferSettingsActions } from 'store/actions';
import currencyformatter from 'utils/currencyformatter';
import exportIcon from 'imgs/export.svg';
import AddRule from './components/AddRule';

type Props = {
  loading: Boolean,
  selectedAccount: Object,
  test: Boolean,
  location: {
    search: String,
  },
  history: {
    push: Function,
  },
  transferRules: Array,
  transferRulesTotalElements: Number,
  fetchTransferRules: Function,
  deleteRule: Function,
  transferApprovalStatus: {
    value: String | null,
    message: String | null,
    type: String | null,
  },
  setTransferApprovalStatus: Function,
  setTransferApprovalRules: Function,
  transferSettings: Object,
  fetchTransferSetting: Function,
};

const TransferApprovals = (props: Props) => {
  const {
    loading,
    selectedAccount,
    test,
    location,
    history,
    transferRules,
    transferRulesTotalElements,
    fetchTransferRules,
    deleteRule,
    transferApprovalStatus,
    setTransferApprovalStatus,
    setTransferApprovalRules,
    transferSettings,
    fetchTransferSetting,
  } = props;

  const [pagination, setPagination] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [ruleGid, setRuleGid] = useState(null);

  const fileExportHeaders = useMemo(() => ([
    { label: 'Amount', key: 'amount' },
    { label: 'Authorizer', key: 'authorizer' },
  ]), []);

  const fileExportData = useMemo(() => (
    transferRules.map((item) => ({
      amount: Number.isNaN(Number(item.amount)) ? item.amount : currencyformatter(Number(item.amount) / 100, selectedAccount?.currency?.name),
      authorizer: item.authorizer.reduce((total, authorizer) => {
        if (total === '') {
          return authorizer.name;
        }
        return `${total}, ${authorizer.name}`;
      }, ''),
    }))
  ), [transferRules]);

  // get the data for the table
  const getData = (query) => {
    const {
      pageNum = 1,
      sortBy = 'approvalAmount',
      direction = 'ASC',
    } = queryString.parse(query);
    fetchTransferRules({
      pageNum,
      sortBy,
      direction,
    });
    fetchTransferSetting({});
  };

  useEffect(() => {
    getData(location.search);
  }, [
    location.search,
    test,
    selectedAccount,
  ]);

  const handleTableChange = (page, filters, sorter) => {
    const selectPageNumber = page.current;
    let params = `&pageNum=${selectPageNumber}`;
    if (sorter && sorter.order) {
      const sortDirection = sorter.order === 'ascend' ? 'ASC' : 'DESC';
      // params += `&sortBy=${sorter.field}&direction=${sortDirection}`;
      params += `&sortBy=approvalAmount&direction=${sortDirection}`;
    }
    history.push({
      pathname: PAGES.TRANSFER_APPROVAL_RULES,
      search: params,
    });
  };

  const handleRuleEdit = (record) => {
    setEditMode(true);
    setRuleGid(record.gid);
    setShowAddModal(true);
  };

  const handleRuleDelete = (record) => {
    deleteRule({
      gid: record.gid,
    });
  };

  // remove the entry from the table if the delete was successful
  useEffect(() => {
    if (transferApprovalStatus.message === TRANSFER_APPROVAL_STATUSES.SUCCESS
      && transferApprovalStatus.type === TRANSFER_APPROVAL_STATUSES.DELETE_TYPE
    ) {
      setTransferApprovalRules(transferRules.filter(item => item.gid !== transferApprovalStatus.value));
      setTransferApprovalStatus({
        message: null,
        type: null,
        value: null,
      });
    } else if (transferApprovalStatus.message === TRANSFER_APPROVAL_STATUSES.FAIL
      && transferApprovalStatus.type === TRANSFER_APPROVAL_STATUSES.DELETE_TYPE
    ) {
      setTransferApprovalStatus({
        message: null,
        type: null,
        value: null,
      });
    }
  }, [transferApprovalStatus]);

  // handle pagination
  useEffect(() => {
    const parsed = queryString.parse(location.search);
    const currentPage = parsed.pageNum || 1;
    setPagination(existingElements => ({
      ...existingElements,
      total: transferRulesTotalElements,
      current: parseInt(currentPage, 10),
      showTotal: total => `Total ${total} items`,
    }));
  }, [
    location.search,
    transferRules,
    transferRulesTotalElements,
  ]);

  const handleCsvFileDownload = (e) => {
    if (fileExportData.length === 0) {
      message.error('No Data to Export!');
      e.preventDefault();
    } else {
      message.success('Starting Download...');
    }
  };

  const columns = [
    {
      title: 'APPROVAL FLAG',
      dataIndex: 'amount',
      width: '25%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (text, record) => (
        record.gid === 'AllFlags'
          ? (
            <span style={{ cursor: 'pointer' }}>
              <SpText>All Flags</SpText>
            </span>
          ) : (
            <span style={{ cursor: 'pointer' }}>
              { currencyformatter(Number(text) / 100, selectedAccount?.currency?.name) || <>&#8211;</> }
            </span>
          )
      ),
    },
    {
      title: 'AUTHORIZER',
      dataIndex: 'authorizer',
      width: '50%',
      align: 'left',
      // sorter: true,
      // sortDirections: ['ascend', 'descend', 'ascend'],
      render: (authorizers) => (
        <span style={{ cursor: 'pointer' }}>
          { authorizers.reduce((total, authorizer) => {
            if (total === '') {
              return `${authorizer.name}`;
            }
            return `${total}, ${authorizer.name}`;
          }, '')
            || <>&#8211;</> }
        </span>
      ),
    },
    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      width: '25%',
      align: 'left',
      render: (_, record) => (
        record.gid === 'AllFlags'
          ? (
            <></>
          ) : (
            <span>
              <RBAC
                permit={[
                  ROLES.OWNER,
                  ROLES.SUPERUSER,
                  ROLES.INTERNALSUPPORT,
                  ROLES.ONBOARDING,
                ]}
              >
                <Button
                  style={{
                    border: 'none',
                    backgroundColor: 'transparent',
                  }}
                  onClick={() => handleRuleEdit(record)}
                >
                  <Icon type="edit" />
                </Button>
                <Button
                  style={{
                    border: 'none',
                    backgroundColor: 'transparent',
                  }}
                  onClick={() => handleRuleDelete(record)}
                >
                  <Icon type="delete" />
                </Button>
              </RBAC>
            </span>
          )
      ),
    },
  ];

  if (!transferSettings?.enableApprovals) {
    return <></>;
  }

  return (
    <Card className="mb-2">
      <RBAC
        permit={[
          ROLES.OWNER,
          ROLES.SUPERUSER,
          ROLES.INTERNALSUPPORT,
          ROLES.ONBOARDING,
          ROLES.TRANSFERSUPPORT,
          ROLES.TRANSFERAPPROVER,
        ]}
        fallback={
          <div>No Permission</div>
        }
      >
        <Spin
          spinning={loading}
          tip="Please wait..."
          size="large"
          delay={300}
        >
          <Row type="flex" justify="end">
            <RBAC
              permit={[
                ROLES.OWNER,
                ROLES.SUPERUSER,
                ROLES.INTERNALSUPPORT,
                ROLES.ONBOARDING,
              ]}
            >
              <Col>
                <SpButton
                  type="secondary"
                  shape="round"
                  className="mr-4 mt-3"
                  ghost
                  onClick={() => setShowAddModal(true)}
                >
                  {'\u002B'}&nbsp;Add Rule
                </SpButton>
              </Col>
            </RBAC>
            <Col style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginRight: '20px',
            }}
            >
              <CSVLink
                data={fileExportData}
                headers={fileExportHeaders}
                filename={`Transfer_Center_Approval_Rules_${selectedAccount?.gid}`}
                className="mt-3"
                onClick={handleCsvFileDownload}
              >
                <img src={exportIcon} alt="Export as CSV" />
              </CSVLink>
            </Col>
          </Row>
          <Row>
            <Table
              columns={columns}
              rowKey={record => record.gid}
              dataSource={transferRules}
              pagination={pagination}
              onChange={handleTableChange}
              style={{ margin: '20px' }}
            />
          </Row>
        </Spin>
        <Row>
          {
            showAddModal && (
            <AddRule
              showAddModal={showAddModal}
              setShowAddModal={setShowAddModal}
              editMode={editMode}
              setEditMode={setEditMode}
              ruleGid={ruleGid}
            />
            )
          }
        </Row>
      </RBAC>
    </Card>
  );
};

const mapStateToProps = state => ({
  loading: state.loading.loading,
  selectedAccount: state.account.selectedAccount,
  test: state.account.test,
  transferRules: state.transferApproval.transferRules,
  transferRulesTotalElements: state.transferApproval.transferRulesTotalElements,
  transferApprovalStatus: state.transferApproval.status,
  transferSettings: state.transferSettings.content,
});

const mapDispatchToProps = dispatch => ({
  fetchTransferSetting: gid => dispatch(transferSettingsActions.fetchTransferSetting({
    payload: gid,
  })),
  fetchTransferRules: param => dispatch(transferApprovalActions.fetchTransferRules({
    payload: param,
  })),
  setTransferApprovalRules: param => dispatch(transferApprovalActions.setTransferApprovalRules({
    payload: param,
  })),
  deleteRule: param => dispatch(transferApprovalActions.deleteRule({
    payload: param,
  })),
  setTransferApprovalStatus: param => dispatch(transferApprovalActions.setStatus({
    payload: param,
  })),
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps,
)(TransferApprovals));
